import { z } from 'zod'

/**
 * Client environment variable validation schema
 *
 * Contains only NEXT_PUBLIC_* variables that are safe to use in client-side code.
 * These variables are embedded in the client bundle and visible in the browser.
 *
 * @see {@link clientEnv} for the validated client environment object
 */
export const clientEnvSchema = z.object({
  // Public variables (accessible in browser, prefixed with NEXT_PUBLIC_)
  NEXT_PUBLIC_APP_NAME: z
    .string()
    .min(1, 'Application name must not be empty')
    .describe('Application display name (required)'),

  NEXT_PUBLIC_APP_URL: z
    .string()
    .url()
    .optional()
    .describe('Public application URL (e.g., https://app.example.com)'),

  NEXT_PUBLIC_APP_ENV: z
    .enum(['dev', 'preview', 'staging', 'production', 'ci', 'test'])
    .describe('Application environment (dev, preview, staging, production, ci, or test)'),

  NEXT_PUBLIC_POSTHOG_KEY: z
    .string()
    .min(1, 'NEXT_PUBLIC_POSTHOG_KEY must not be empty when set')
    .optional()
    .describe(
      'PostHog project token (client + server capture). Per-Vercel-env scoping: 3 distinct values (mealplan-production / mealplan-staging / mealplan-development). Unset = PostHog disabled.',
    ),

  NEXT_PUBLIC_POSTHOG_HOST: z
    .string()
    .url('NEXT_PUBLIC_POSTHOG_HOST must be a valid URL')
    .optional()
    .describe(
      'PostHog ingest host (e.g. https://eu.i.posthog.com). Identical across all environments. Distinct from POSTHOG_CLI_HOST (admin host).',
    ),
})

/**
 * Server-only environment variable validation schema
 *
 * Contains only server-side variables (not prefixed with NEXT_PUBLIC_).
 * These are validated lazily when accessed, not at module load time.
 */
const serverOnlyEnvSchema = z.object({
  BETTER_AUTH_SECRET: z
    .string()
    .min(32, 'BETTER_AUTH_SECRET must be at least 32 characters for security')
    .describe('Secret key for Better Auth (generate with: openssl rand -base64 32)'),

  DATABASE_URL: z
    .string()
    .url()
    .describe('Database connection string for Prisma (pooled connection for Neon)'),

  DATABASE_URL_UNPOOLED: z
    .string()
    .url()
    .describe('Direct database connection string for migrations (non-pooled for Neon)'),

  ANTHROPIC_API_KEY: z
    .string()
    .min(1, 'ANTHROPIC_API_KEY is required for AI features')
    .describe('Anthropic API key for Claude AI integration'),

  RESEND_API_KEY: z
    .string()
    .min(1, 'RESEND_API_KEY is required for email sending')
    .optional()
    .describe(
      'Resend API key for sending emails (get from https://resend.com/api-keys). FROM addresses are code constants in src/lib/resend.ts (EMAIL_SENDERS) — see docs/EMAIL_SETUP.md.',
    ),

  UPSTASH_REDIS_REST_URL: z
    .string()
    .url('UPSTASH_REDIS_REST_URL must be a valid URL')
    .describe(
      'Upstash Redis REST URL. Must be set manually in Vercel (Marketplace integration injects KV_* names instead); see docs/ENVIRONMENT_SETUP.md § "Upstash Redis".',
    ),

  UPSTASH_REDIS_REST_TOKEN: z
    .string()
    .min(1, 'UPSTASH_REDIS_REST_TOKEN is required for rate limiting')
    .describe(
      'Upstash Redis REST token. Must be set manually in Vercel (Marketplace integration injects KV_* names instead); see docs/ENVIRONMENT_SETUP.md § "Upstash Redis".',
    ),

  STATUS_INCIDENT_MESSAGE: z
    .string()
    .optional()
    .describe(
      'Operator-set banner shown on /status during an incident. Empty/unset means no banner. See docs/RUNBOOKS/status-page.md.',
    ),

  E2E_DISABLE_RATE_LIMIT: z
    .enum(['1', 'true', '0', 'false'])
    .optional()
    .describe(
      'E2E-only bypass for the abuse-sensitive rate limiter (CI sign-up tests collide on the shared runner IP). Only activates when NEXT_PUBLIC_APP_ENV is one of ci/test/dev; any other value (production, staging, preview, unset, or typo) throws at module init — see src/lib/rate-limit.ts.',
    ),

  SIGNUP_TIMING_LOG: z
    .enum(['1', 'true', '0', 'false'])
    .optional()
    .describe(
      'Diagnostics-only: log per-step sign-up timings (hibp / scrypt / invite-code / total) to stderr. Off unless 1/true; the local E2E runner (scripts/e2e-local.sh) sets it — see src/lib/signup-timing.ts (HON-569).',
    ),

  POSTHOG_CLI_HOST: z
    .string()
    .url('POSTHOG_CLI_HOST must be a valid URL')
    .optional()
    .describe(
      'PostHog admin host for posthog-cli (e.g. https://eu.posthog.com). Identical across all environments. Distinct from the ingest host — swapping them produces misleading "invalid project ID" errors. Build-time only.',
    ),

  POSTHOG_CLI_PROJECT_ID: z
    .string()
    .regex(/^\d+$/, 'POSTHOG_CLI_PROJECT_ID must be a numeric project ID')
    .optional()
    .describe(
      'Numeric PostHog project ID for posthog-cli sourcemap upload. Per-Vercel-env scoping: 3 distinct values, one per PostHog project. Build-time only.',
    ),

  POSTHOG_CLI_API_KEY: z
    .string()
    .min(1, 'POSTHOG_CLI_API_KEY must not be empty when set')
    .optional()
    .describe(
      'PostHog personal API key with sourcemap:write scope for posthog-cli. Identical across all environments (one key works for all three projects). Build-time only. Distinct from NEXT_PUBLIC_POSTHOG_KEY (project token used for event capture).',
    ),

  ADMIN_EMAIL: z
    .string()
    .email('ADMIN_EMAIL must be a valid email address')
    .describe(
      'Email of the single beta admin. Used by isAdmin(session) in src/lib/auth-helpers.ts to gate /admin/signup-codes (HON-488). One person for invite-only beta — switch to a role-based check before opening up admin access.',
    ),

  CRON_SECRET: z
    .string()
    .min(32, 'CRON_SECRET must be at least 32 characters for security')
    .optional()
    .describe(
      'Shared secret for Vercel Cron authentication (HON-481). Vercel auto-injects "Authorization: Bearer <CRON_SECRET>" on scheduled invocations when this env var is set; the purge cron route (/api/cron/purge-deleted-users) rejects any request without it. Optional in dev (cron effectively disabled), but the route returns 500 if unset in production. Generate with: openssl rand -base64 32',
    ),
})

/**
 * Complete server environment variable validation schema
 *
 * Combines client and server-only variables for type inference.
 *
 * @see {@link serverEnv} for the validated server environment object
 */
export const serverEnvSchema = clientEnvSchema.merge(serverOnlyEnvSchema)

/**
 * Validated client-side environment variables
 *
 * Safe to use in client components and browser code. Only contains NEXT_PUBLIC_* variables.
 * Throws an error during module load if validation fails.
 *
 * @example
 * ```typescript
 * import { clientEnv } from '@/lib/env'
 *
 * console.log(clientEnv.NEXT_PUBLIC_APP_NAME)  // 'My App'
 * ```
 *
 * @throws {Error} If client environment validation fails
 */
export const clientEnv = (() => {
  // Explicitly reference env vars so they survive Next.js client bundling
  const envVars = {
    NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  }

  const parsed = clientEnvSchema.safeParse(envVars)

  if (!parsed.success) {
    const fieldErrors = parsed.error.flatten().fieldErrors
    const errorMessages = Object.entries(fieldErrors)
      .map(([field, errors]) => {
        const messages = (errors ?? []).join(', ')
        return `  • ${field}: ${messages}`
      })
      .join('\n')

    // eslint-disable-next-line no-console
    console.error('❌ Invalid client environment variables:\n' + errorMessages)

    throw new Error(
      `Invalid environment variables. Check your .env and .env.local files.\n${errorMessages}`,
    )
  }

  return parsed.data
})()

/**
 * Validated server-side environment variables
 *
 * Includes both NEXT_PUBLIC_* and server-only variables. Only use in server components,
 * API routes, and server-side code.
 *
 * Uses lazy validation: server-only variables are validated when accessed, not at module
 * load time. This allows the module to be bundled for the client without errors, while
 * still enforcing that required variables exist when actually used on the server.
 *
 * @example
 * ```typescript
 * import { serverEnv } from '@/lib/env'
 *
 * console.log(serverEnv.BETTER_AUTH_SECRET)  // Validated on access
 * console.log(serverEnv.NEXT_PUBLIC_APP_NAME)  // Also available
 * ```
 *
 * @throws {Error} If a server-only variable is undefined when accessed
 */
export const serverEnv = new Proxy(
  {
    // Client vars are already validated
    ...clientEnv,
    // Server-only vars from process.env (validated on access)
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_URL_UNPOOLED: process.env.DATABASE_URL_UNPOOLED,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    STATUS_INCIDENT_MESSAGE: process.env.STATUS_INCIDENT_MESSAGE,
    E2E_DISABLE_RATE_LIMIT: process.env.E2E_DISABLE_RATE_LIMIT,
    SIGNUP_TIMING_LOG: process.env.SIGNUP_TIMING_LOG,
    POSTHOG_CLI_HOST: process.env.POSTHOG_CLI_HOST,
    POSTHOG_CLI_PROJECT_ID: process.env.POSTHOG_CLI_PROJECT_ID,
    POSTHOG_CLI_API_KEY: process.env.POSTHOG_CLI_API_KEY,
    ADMIN_EMAIL: process.env.ADMIN_EMAIL,
    CRON_SECRET: process.env.CRON_SECRET,
  } as z.infer<typeof serverEnvSchema>,
  {
    get(target, prop) {
      const value = target[prop as keyof typeof target]

      // For server-only variables (not NEXT_PUBLIC_*), validate when accessed
      if (typeof prop === 'string' && !prop.startsWith('NEXT_PUBLIC_')) {
        // Parse just this field to get validation errors
        const fieldSchema =
          serverOnlyEnvSchema.shape[prop as keyof typeof serverOnlyEnvSchema.shape]

        if (fieldSchema) {
          const result = fieldSchema.safeParse(value)

          if (!result.success) {
            const errorMessage = result.error.issues.map((e) => e.message).join(', ')
            // eslint-disable-next-line no-console
            console.error(`❌ Invalid server environment variable ${String(prop)}: ${errorMessage}`)

            throw new Error(
              `Invalid environment variable ${String(prop)}: ${errorMessage}\n` +
                `Check your .env and .env.local files and restart the dev server.`,
            )
          }

          return result.data
        }
      }

      return value
    },
  },
)

/**
 * Type for client environment variables
 */
export type ClientEnv = typeof clientEnv

/**
 * Type for server environment variables
 */
export type ServerEnv = z.infer<typeof serverEnvSchema>

/**
 * Gets the base URL for client-side code (browser)
 *
 * Priority:
 * 1. NEXT_PUBLIC_APP_URL (explicitly set in env)
 * 2. NEXT_PUBLIC_VERCEL_URL (automatically set by Vercel for all deployments)
 * 3. http://localhost:3000 (local development fallback)
 *
 * @returns The base URL as a string
 */
export function getClientBaseURL(): string {
  if (clientEnv.NEXT_PUBLIC_APP_URL) {
    return clientEnv.NEXT_PUBLIC_APP_URL
  }

  // Vercel sets NEXT_PUBLIC_VERCEL_URL automatically for all deployments
  if (process.env.NEXT_PUBLIC_VERCEL_URL) {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
  }

  return 'http://localhost:3000'
}

/**
 * Gets the base URL for server-side code (Node.js)
 *
 * Priority:
 * 1. NEXT_PUBLIC_APP_URL (explicitly set in env)
 * 2. VERCEL_URL (automatically set by Vercel for all deployments)
 * 3. http://localhost:3000 (local development fallback)
 *
 * @returns The base URL as a string
 */
export function getServerBaseURL(): string {
  if (serverEnv.NEXT_PUBLIC_APP_URL) {
    return serverEnv.NEXT_PUBLIC_APP_URL
  }

  // Vercel sets VERCEL_URL automatically for all deployments
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`
  }

  return 'http://localhost:3000'
}
